import moment from 'moment'
import { createSelector } from 'reselect'
import { selectPremiumStripeKey, selectPremiumUpsellURL } from 'services/bootup/selectors'

export const selectPremium = (state) => state.premium

export const selectSubmitting = (state) => state.premium.submitting
export const selectSuccess = (state) => state.premium.success
export const selectProducts = (state) => state.premium.products
export const selectError = (state) => state.premium.error
export const selectErrorCode = (state) => state.premium.errorCode
export const selectErrorDescription = (state) => state.premium.error_description
export const selectCheckoutPageLoading = (state) => state.premium.checkoutPageLoading
export const selectSubscriptionSettingsLoading = (state) => state.premium.subscriptionSettingsLoading
export const selectUserCoupon = (state) => state.premium.userCoupon
export const selectUserCoupons = (state) => state.premium.userCoupons
export const selectLoadError = (state) => state.premium.loadError
export const selectAttemptedCoupon = (state) => state.premium.attemptedCoupon
export const selectBillingProfileSuccess = (state) => state.premium.billingProfileSuccess
export const selectSubmittingAction = (state) => state.premium.submittingAction
export const selectAutoRenewalLoading = (state) => state.premium.autoRenewalLoading
export const selectAutoRenewalSuccess = (state) => state.premium.autoRenewalSuccess
export const selectAutoRenewalError = (state) => state.premium.autoRenewalError

export const selectUpsellURL = selectPremiumUpsellURL
export const selectStripeKey = selectPremiumStripeKey
export const selectCreditCardForm = (name) => (state) => state.form[name] || null
export const selectCreditCardFields = (formName) => createSelector(
  selectCreditCardForm(formName),
  (form) => form && form.fields
)

// coupons
// ============================================================================

export const selectCoupon = (state) => state.premium.coupon

export const selectCouponCode = createSelector(
  selectCoupon,
  (coupon) => coupon && coupon.id,
)

export const selectCouponInterval = createSelector(
  selectCoupon,
  (coupon) => coupon && coupon.duration_interval,
)

export const selectCouponUnit = createSelector(
  selectCoupon,
  (coupon) => coupon && coupon.duration_unit,
)

// subs
// =============================================================================

export const selectSubscriptions = (state) => state.premium.subscriptions

export const selectSubscription = createSelector(
  selectSubscriptions,
  (subs) => subs && subs.find((sub) => sub.subscription_status === 'active')
)

export const selectSubId = createSelector(
  selectSubscription,
  (sub) => sub && sub.subscription_id,
)

export const selectSubCurrency = createSelector(
  selectSubscription,
  (sub) => sub && sub.payment_details.price_point.currency
)

export const selectSubPlatform = createSelector(
  selectSubscription,
  (sub) => sub && sub.payment_details.platform_details.platform_name,
)

export const selectSubIsMobile = createSelector(
  selectSubPlatform,
  (platform) => platform && (platform === 'android' || platform === 'ios')
)

export const selectSubIsPromo = createSelector(
  selectSubscription,
  (sub) => sub && sub.payment_details.platform_details.payment_providers[0] === 'myfitnesspal',
)

export const selectSubIsTrial = createSelector(
  selectSubscription,
  (sub) => sub && sub.is_trial,
)

export const selectSubInterval = createSelector(
  selectSubscription,
  (sub) => sub && sub.subscription_details.frequency_interval,
)

export const selectSubUnit = createSelector(
  selectSubscription,
  (sub) => sub && sub.subscription_details.frequency_unit,
)

export const selectSubTrialInterval = createSelector(
  selectSubscription,
  (sub) => sub && sub.subscription_details.trial_details.duration_interval,
)

export const selectSubTrialUnit = createSelector(
  selectSubscription,
  (sub) => sub && sub.subscription_details.trial_details.duration_unit,
)

export const selectSubStartDate = createSelector(
  selectSubscription,
  (sub) => sub && moment(sub.subscription_start_date),
)

export const selectSubEndDate = createSelector(
  selectSubscription,
  selectSubStartDate,
  selectSubInterval,
  selectSubUnit,
  (sub, subStartDate, subInterval, subUnit) => {
    if (!sub) { return sub }

    if (sub.subscription_start_date === sub.subscription_end_date) {
      return subStartDate.clone().add(subInterval, subUnit)
    }

    return moment(sub.subscription_end_date)
  }
)

export const selectSubDuration = createSelector(
  selectSubscription,
  selectSubInterval,
  selectSubUnit,
  selectSubStartDate,
  selectSubEndDate,
  (sub, subInterval, subUnit, subStartDate, subEndDate) => {
    if (!sub) { return null }
    const duration = subEndDate - subStartDate
    // moment durations are always given in milliseconds,
    // but our FormattedDuration call on the page expects seconds
    // generally our API uses seconds for durations
    return moment.duration(duration) / 1000
  }
)

export const selectSubAutoRenew = createSelector(
  selectSubscription,
  (sub) => sub && !sub.requested_cancellation,
)

export const selectSubAmount = createSelector(
  selectSubscription,
  (sub) => sub && sub.payment_details.price_point.amount,
)

// products
// ============================================================================

export const selectProduct = (state) => state.premium.selectedProduct

export const selectProductAmount = createSelector(
  selectProduct,
  (product) => {
    return product && product.availability_details[0].price_point.amount
  },
)

export const selectTrialInterval = createSelector(
  selectProduct,
  (product) => product && product.subscription_details.trial_details.duration_interval
)

export const selectTrialUnit = createSelector(
  selectProduct,
  (product) => product && product.subscription_details.trial_details.duration_unit
)

export const selectProductInterval = createSelector(
  selectProduct,
  (product) => product && product.subscription_details.frequency_interval
)

export const selectProductUnit = createSelector(
  selectProduct,
  (product) => product && product.subscription_details.frequency_unit
)

export const selectIsPromo = createSelector(
  selectProduct,
  (product) => {
    return product && product.availability_details[0].available_platforms[0].payment_providers[0] === 'myfitnesspal'
  }
)

export const selectIsTrial = createSelector(
  selectProduct,
  selectIsPromo,
  selectTrialInterval,
  selectCouponCode,
  selectCouponInterval,
  (product, isPromo, trialInterval, coupon, couponInterval) => {
    const isCouponTrial = !isPromo && coupon && couponInterval > 0
    const isProductTrial = !isPromo && product && !coupon && trialInterval > 0
    return isCouponTrial || isProductTrial
  }
)

export const selectProductFreeInterval = createSelector(
  selectProduct,
  selectCouponCode,
  selectTrialInterval,
  selectCouponInterval,
  selectProductInterval,
  (product, coupon, trialInterval, couponInterval, productInterval) => {
    if (product && coupon) {
      if (couponInterval === 0) { return productInterval }
      return couponInterval
    }
    if (product) { return trialInterval }
  }
)

export const selectProductFreeUnit = createSelector(
  selectProduct,
  selectCouponCode,
  selectTrialUnit,
  selectCouponUnit,
  selectCouponInterval,
  selectProductUnit,
  (product, coupon, trialUnit, couponUnit, couponInterval, productUnit) => {
    if (product && coupon) {
      if (couponInterval === 0) { return productUnit }
      return couponUnit
    }
    if (product) { return trialUnit }
  }
)

export const selectProductFreeEndDate = createSelector(
  selectProduct,
  selectProductFreeInterval,
  selectProductFreeUnit,
  (product, interval, unit) => {
    return moment().add(interval, unit)
  }
)

export const selectProductEndDate = createSelector(
  selectProduct,
  selectProductInterval,
  selectProductUnit,
  selectIsPromo,
  selectIsTrial,
  selectProductFreeInterval,
  selectProductFreeUnit,
  (product, productInterval, productUnit, isPromo, isTrial, freeInterval, freeUnit) => {
    if (!product) { return null }

    if (isPromo || isTrial) {
      return moment().add(freeInterval, freeUnit)
    }

    return moment().add(productInterval, productUnit)
  }
)

export const selectCurrency = createSelector(
  selectProduct,
  (product) => product && product.availability_details[0].price_point.currency
)

export const selectAmount = createSelector(
  selectProduct,
  selectIsTrial,
  (product) => product && product.availability_details[0].price_point.amount,
)

// billing profiles
// ============================================================================

export const selectBillingProfiles = (state) => state.premium.billingProfiles
export const selectUpdatingBillingProfile = (state) => state.premium.updatingBillingProfile

export const selectHasDefaultBillingProfile = createSelector(
  selectBillingProfiles,
  (profiles) => {
    const exists = profiles && profiles.length > 0
    return exists && profiles.some((profile) => profile.default)
  }
)

export const selectDefaultBillingProfile = createSelector(
  selectBillingProfiles,
  (profiles) => profiles && profiles.find((profile) => profile.default)
)

export const selectPaymentRequired = createSelector(
  selectIsPromo,
  (isPromo) => !isPromo
)

export const selectPremiumStatus = createSelector(
  selectPremium,
  (premium) => premium && premium.status && premium.status.result && premium.status.result.output,
)

export const selectIsPremium = createSelector(
  selectPremiumStatus,
  (status) => status && status.days_remaining === -1,
)

export const selectIsAdFree = createSelector(
  selectPremiumStatus,
  (status) => status && status.is_ad_free,
)

export const selectShouldSeeAds = createSelector(
  selectIsPremium,
  selectIsAdFree,
  (isPremium, isAdFree) => !isPremium && !isAdFree,
)



// WEBPACK FOOTER //
// ./src/services/premium/selectors.js